import {
    Button,
    Link,
    makeStyles,
    mergeClasses,
    tokens,
    Tooltip,
    Tree,
    TreeCheckedChangeData,
    TreeCheckedChangeEvent,
    TreeItem,
    TreeItemLayout,
    TreeOpenChangeData,
    TreeOpenChangeEvent,
} from '@fluentui/react-components';
import {
    DocumentAdd16Regular,
    DocumentText20Filled,
    DocumentText20Regular,
    Folder20Regular,
    Form20Filled,
    Form20Regular,
} from '@fluentui/react-icons';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../../api/features/features';
import { StatementFilled, StatementRegular } from '../../../assets/icons';
import { useFormView } from '../../../contexts/useFormView';
import { TaxReturnTOCItem } from '../../../model';
import { customPalette } from '../../../theme';
import { DetailedWorkspacesEnabled } from '../../../utils/FeatureFlags.constants';
import { getFirstVisibleChildId } from '../../../utils/formNavigation';
import { getTocItemFormPageName, groupTocItemsById, resolveTocItemFromCandidates } from '../../../utils/TaxReturnTOCUtils';
import { PINNED_SEARCH_PARAM_NAME } from '../../../utils/Urls.constant';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import { SvgIcon } from '../../common/SvgIcon';

export interface FormTOCControlledTreeViewProps {
    filteredItems: TaxReturnTOCItem[];
    noRecordsMessage: string;
    selectedNodeId?: string;
    selectedFormPageName?: string | null;
}

const useStyles = makeStyles({
    noRecordsLabel: {
        margin: '20px',
        fontSize: '14px',
    },
    rowTitle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '280px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        height: '38px',
        textDecoration: 'none',
        color: 'inherit',
    },
    textNoWrap: {
        whiteSpace: 'nowrap',
    },
    selectorHidden: {
        display: 'none',
    },
    linkDisabled: {
        pointerEvents: 'none',
        cursor: 'default',
        color: tokens.colorNeutralForegroundDisabled,
    },
    disabledText: {
        color: tokens.colorNeutralForegroundDisabled,
    },
    treeDisabled: {
        opacity: 0.55,
        pointerEvents: 'none',
    },
    selectedRow: {
        backgroundColor: customPalette.statusCoolLightGrey,
    },
    icon: {
        color: customPalette.blue075A92,
    },
    iconDisabled: {
        color: tokens.colorNeutralForegroundDisabled,
    },
});

const FormTOCControlledTreeView: React.FC<FormTOCControlledTreeViewProps> = ({
    filteredItems,
    noRecordsMessage,
    selectedNodeId,
    selectedFormPageName,
}) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const {
        isDrawerPinned,
        isLoading,
        openAddStatementModal,
        navigateToTocItemFromTree,
        setTocNodeExpanded,
        getCurrentFormPageUrl,
        emptyDetailedWorkspaceItemsMap,
        tocTreeState,
    } = useFormView();
    const { data: isDetailedWorkspacesEnabled } = useIsFeatureEnabled({ featureFlagName: DetailedWorkspacesEnabled });
    const selectedRowClassName = styles.selectedRow;
    const iconClassName = mergeClasses(styles.icon, isLoading && styles.iconDisabled);

    const itemsById: Map<string, TaxReturnTOCItem[]> = useMemo(() => groupTocItemsById(filteredItems), [filteredItems]);

    const handleTreeItemNavigate = (item: TaxReturnTOCItem): void => {
        if (isLoading) {
            return;
        }

        navigateToTocItemFromTree(item);
        if (item.id) {
            setTocNodeExpanded(item.id, true);
        }
    };

    const handleTreeOpenChange = (_event: TreeOpenChangeEvent, data: TreeOpenChangeData): void => {
        if (isLoading) {
            return;
        }

        const nodeId: string = data.value.toString();
        setTocNodeExpanded(nodeId, data.open);
    };

    const handleTreeCheckedChange = (event: TreeCheckedChangeEvent, data: TreeCheckedChangeData): void => {
        if (isLoading) {
            return;
        }

        const nodeId: string = data.value.toString();
        const candidates: TaxReturnTOCItem[] | undefined = itemsById.get(nodeId);

        const formPageNameFromDom: string | null | undefined =
            candidates && candidates.length > 1
                ? (event.target as HTMLElement | null)?.closest?.('[data-toc-form-page-name]')?.getAttribute('data-toc-form-page-name')
                : undefined;
        const element: TaxReturnTOCItem | undefined = resolveTocItemFromCandidates(candidates, formPageNameFromDom);

        if (element) {
            handleTreeItemNavigate(element);
        }
    };

    const addStatement = (event: React.MouseEvent<HTMLButtonElement>, elementId: string) => {
        openAddStatementModal(elementId);
        event.stopPropagation();
    };

    const addStatementsButtonTemplate = (elementId: string) => {
        const isDisabled: boolean = !emptyDetailedWorkspaceItemsMap || emptyDetailedWorkspaceItemsMap.size === 0;

        return (
            <Tooltip
                content={isDisabled ? t('noStatementsAvailableToAdd').toString() : t('addStatement').toString()}
                relationship='description'
                appearance='inverted'
                withArrow
                positioning='before'
            >
                <Button
                    size='small'
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => addStatement(event, elementId)}
                    onClickCapture={(event: React.MouseEvent<HTMLButtonElement>) => {
                        if (isDisabled) {
                            event.stopPropagation();
                        }
                    }}
                    aria-label={t('add').toString()}
                    icon={<DocumentAdd16Regular />}
                    disabled={isDisabled || isLoading}
                    disabledFocusable={isLoading || isDisabled}
                >
                    {t('add').toString()}
                </Button>
            </Tooltip>
        );
    };

    const renderNodeContent = (
        url: string,
        elementTitle: string,
        element: TaxReturnTOCItem,
        isSelected?: boolean,
        hasChildren?: boolean
    ) => {
        const isStatement: boolean = element?.isStatement ?? false;
        const isDiagnostic: boolean = element?.isDiagnostic ?? false;
        let nodeIcon: React.ReactNode = undefined;

        if (hasChildren) {
            if (element.parentId === null) {
                nodeIcon = <Folder20Regular className={iconClassName} />;
            } else {
                nodeIcon = isSelected ? <Form20Filled className={iconClassName} /> : <Form20Regular className={iconClassName} />;
            }
        }

        return (
            <div className={styles.row}>
                <Link
                    appearance='subtle'
                    href={url}
                    tabIndex={isLoading ? -1 : undefined}
                    aria-disabled={isLoading}
                    className={isLoading ? styles.linkDisabled : undefined}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleTreeItemNavigate(element);
                    }}
                >
                    <span className={styles.row}>
                        {isDiagnostic ? (
                            isSelected ? (
                                <DocumentText20Filled className={iconClassName} />
                            ) : (
                                <DocumentText20Regular className={iconClassName} />
                            )
                        ) : undefined}

                        {isStatement && element.parentId !== null ? (
                            <SvgIcon SvgComponent={isSelected ? StatementFilled : StatementRegular} className={iconClassName} />
                        ) : undefined}

                        {nodeIcon}
                        <DefaultTooltipHostV9
                            appearance='inverted'
                            withArrow
                            positioning='before'
                            content={elementTitle}
                            containerClassName={styles.rowTitle}
                        >
                            <span className={mergeClasses(styles.textNoWrap, isLoading ? styles.disabledText : undefined)}>
                                {elementTitle}
                            </span>
                        </DefaultTooltipHostV9>
                    </span>
                </Link>
                {isDetailedWorkspacesEnabled && element.parentId === null && isStatement
                    ? addStatementsButtonTemplate(`tree-item-0-${element.id}`)
                    : undefined}
            </div>
        );
    };

    const renderTreeItem = (item: TaxReturnTOCItem, level: number): JSX.Element => {
        const tocItemId: string = item.id ?? '';
        const formPageName: string = getTocItemFormPageName(item);
        const hasChildren: boolean = Boolean(item.children && item.children.length > 0);
        const isSelected: boolean = selectedNodeId === tocItemId && (selectedFormPageName ?? '') === formPageName;
        const treeItemId: string = `tree-item-${level}-${tocItemId}`;

        const linkTargetId = hasChildren ? getFirstVisibleChildId(item) ?? item.id : item.id;
        const linkTargetItem: TaxReturnTOCItem | undefined = hasChildren
            ? item.children?.find((child) => child.id === linkTargetId) ?? item
            : item;
        let url: string = getCurrentFormPageUrl(linkTargetId, getTocItemFormPageName(linkTargetItem));
        if (isDrawerPinned) {
            const separator: string = url.includes('?') ? '&' : '?';
            url = `${url}${separator}${PINNED_SEARCH_PARAM_NAME}=true`;
        }

        const rowClassName: string = mergeClasses(styles.row, isSelected ? selectedRowClassName : undefined);

        return (
            <TreeItem key={treeItemId} id={treeItemId} itemType={hasChildren ? 'branch' : 'leaf'} value={tocItemId}>
                <TreeItemLayout
                    data-testid={treeItemId}
                    data-toc-form-page-name={formPageName}
                    className={rowClassName}
                    selector={{ className: styles.selectorHidden, input: { 'aria-hidden': true } }}
                >
                    {renderNodeContent(url, item.title ?? '', item, isSelected, hasChildren)}
                </TreeItemLayout>
                {item.children ? (
                    <Tree>{item.children.map((child: TaxReturnTOCItem) => renderTreeItem(child, level + 1))}</Tree>
                ) : undefined}
            </TreeItem>
        );
    };

    const hasNoContent: boolean = !filteredItems || filteredItems.length === 0;

    if (hasNoContent) {
        return <span className={styles.noRecordsLabel}>{noRecordsMessage}</span>;
    }

    const openItems: string[] = Array.from(tocTreeState.expandedIds);
    const checkedItems: string[] = selectedNodeId ? [selectedNodeId] : [];

    return (
        <div className={isLoading ? styles.treeDisabled : undefined} data-testid={isLoading ? 'toc-tree-loading' : undefined}>
            <Tree
                data-testid='form-toc-tree'
                aria-label={t('formsTableOfContents')?.toString()}
                aria-disabled={isLoading}
                openItems={openItems}
                onOpenChange={handleTreeOpenChange}
                selectionMode='single'
                checkedItems={checkedItems}
                onCheckedChange={handleTreeCheckedChange}
            >
                {filteredItems.map((item: TaxReturnTOCItem) => renderTreeItem(item, 0))}
            </Tree>
        </div>
    );
};

export default FormTOCControlledTreeView;
